import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  ScanLine,
  Loader2,
  X,
  Save,
  User,
  BadgeInfo,
  Sparkles,
  Eye,
  EyeOff,
} from "lucide-react";
import { analyzeXray, createExamination } from "@/lib/api";
import BoundingBoxOverlay from "@/components/BoundingBoxOverlay";
import ReportStandardSelect from "@/components/ReportStandardSelect";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,.dcm,application/dicom";

const emptyPatient = {
  name: "",
  patient_id: "",
  age: "",
  gender: "",
  exam_date: new Date().toISOString().slice(0, 10),
  notes: "",
};

export default function Analysis() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [result, setResult] = useState(null); // { image_base64, image_width, image_height, predictions, inference_time_ms }
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [patient, setPatient] = useState(emptyPatient);
  const [reportStandard, setReportStandard] = useState("general");
  const [radNotes, setRadNotes] = useState("");
  const [finalDiag, setFinalDiag] = useState("");
  const [showBoxes, setShowBoxes] = useState(true);
  const [hovered, setHovered] = useState(null);

  const preds = result?.predictions ?? [];
  const summary = useMemo(() => {
    const map = new Map();
    for (const p of preds) {
      map.set(p.class, (map.get(p.class) || 0) + 1);
    }
    return Array.from(map.entries()).map(([k, v]) => ({ class: k, count: v }));
  }, [preds]);

  const onFile = (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    const isDicom = name.endsWith(".dcm") || name.endsWith(".dicom") || f.type === "application/dicom";
    if (!isDicom && !f.type.startsWith("image/")) {
      toast.error("File harus gambar (PNG/JPG/WEBP) atau DICOM (.dcm)");
      return;
    }
    setFile(f);
    setResult(null);
    // DICOM files can't be shown as browser preview — set a placeholder
    if (isDicom) {
      setPreview(null);
    } else {
      const url = URL.createObjectURL(f);
      setPreview(url);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const runAnalyze = async () => {
    if (!file) {
      toast.error("Pilih file X-ray terlebih dahulu");
      return;
    }
    setAnalyzing(true);
    try {
      const data = await analyzeXray(file);
      setResult(data);

      // Auto-populate patient form if DICOM header was parsed
      if (data.patient_prefill) {
        const p = data.patient_prefill;
        setPatient((prev) => ({
          name: p.name || prev.name,
          patient_id: p.patient_id || prev.patient_id,
          age: p.age != null ? String(p.age) : prev.age,
          gender: p.gender || prev.gender,
          exam_date: p.exam_date || prev.exam_date,
          notes: p.notes || prev.notes,
        }));
        toast.info("Data pasien di-prefill dari DICOM header", {
          description: `AccessionNumber: ${p.patient_id || "-"} · ${p.name || "-"}`,
          duration: 5000,
        });
      }

      if (data.demo_mode) {
        toast.warning("Demo Mode Active", {
          description: "Model Roboflow belum di-deploy. Menampilkan MOCK predictions untuk validasi UI.",
          duration: 6000,
        });
      } else {
        toast.success(
          `Analisa selesai · ${data.predictions?.length || 0} temuan · ${data.inference_time_ms} ms`
        );
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Analisa gagal";
      toast.error("Analisa gagal", { description: String(msg).slice(0, 200) });
    } finally {
      setAnalyzing(false);
    }
  };

  const runSave = async () => {
    if (!result) {
      toast.error("Belum ada hasil analisa untuk disimpan");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        patient: {
          ...patient,
          age: patient.age === "" ? null : Number(patient.age),
        },
        image_base64: result.image_base64,
        image_width: result.image_width,
        image_height: result.image_height,
        predictions: result.predictions,
        inference_time_ms: result.inference_time_ms,
        radiologist_notes: radNotes,
        final_diagnosis: finalDiag,
        report_standard: reportStandard,
      };
      const saved = await createExamination(payload);
      toast.success("Pemeriksaan tersimpan");
      navigate(`/exam/${saved.id}`);
    } catch (e) {
      toast.error("Gagal menyimpan", {
        description: String(e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
          Workspace
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">
          Analisa X-ray Thoraks
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Upload foto rontgen thoraks, jalankan AI second opinion, lalu tinjau
          bounding box dan simpan sebagai record MCU.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left: patient form */}
        <aside className="xl:col-span-3">
          <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 sticky top-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-indigo-400" />
              <h3 className="font-display text-base font-medium">Data Pasien</h3>
            </div>
            <div className="space-y-3">
              <FormField
                label="Nama Pasien"
                value={patient.name}
                onChange={(v) => setPatient({ ...patient, name: v })}
                testId="patient-name-input"
                placeholder="John Doe"
              />
              <FormField
                label="ID / MR Number"
                value={patient.patient_id}
                onChange={(v) => setPatient({ ...patient, patient_id: v })}
                testId="patient-id-input"
                placeholder="MCU-0001"
                mono
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  label="Umur"
                  value={patient.age}
                  onChange={(v) => setPatient({ ...patient, age: v })}
                  testId="patient-age-input"
                  type="number"
                  placeholder="35"
                />
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                    Gender
                  </label>
                  <select
                    data-testid="patient-gender-select"
                    value={patient.gender}
                    onChange={(e) =>
                      setPatient({ ...patient, gender: e.target.value })
                    }
                    className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  >
                    <option value="">—</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
              <FormField
                label="Tanggal Exam"
                value={patient.exam_date}
                onChange={(v) => setPatient({ ...patient, exam_date: v })}
                testId="patient-date-input"
                type="date"
              />
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                  Catatan Klinis
                </label>
                <textarea
                  data-testid="patient-notes-input"
                  value={patient.notes}
                  onChange={(e) =>
                    setPatient({ ...patient, notes: e.target.value })
                  }
                  rows={3}
                  placeholder="Keluhan, riwayat, dsb."
                  className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                />
              </div>
              <ReportStandardSelect value={reportStandard} onChange={setReportStandard} testId="report-standard-select" />
            </div>
          </div>
        </aside>

        {/* Center: viewer */}
        <section className="xl:col-span-6">
          {!result && !preview && (
            <div
              data-testid="upload-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                onFile(f);
              }}
              onClick={() => fileRef.current?.click()}
              className={`relative shine cursor-pointer group flex flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-all min-h-[560px] ${
                dragOver
                  ? "border-indigo-500 bg-indigo-500/5"
                  : "border-zinc-800 hover:border-indigo-500/60 bg-zinc-900/30"
              }`}
            >
              <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-5 group-hover:bg-indigo-500/20 transition-colors">
                <Upload className="w-6 h-6 text-indigo-400" strokeWidth={1.75} />
              </div>
              <div className="font-display text-xl font-medium text-zinc-100 mb-1">
                Drop foto X-ray atau klik untuk upload
              </div>
              <div className="text-sm text-zinc-500 mb-6">
                PNG · JPG · WEBP · DICOM (.dcm) · maks 20 MB
              </div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-mono">
                Model: RF-DETR NAS Base · Chest MCU
              </div>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                data-testid="upload-file-input"
              />
            </div>
          )}

          {(preview || result || file) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-zinc-400 truncate">
                  <span className="text-zinc-500">File:</span>{" "}
                  <span className="text-zinc-200">{file?.name || "loaded"}</span>
                  {(file?.name?.toLowerCase().endsWith(".dcm") ||
                    file?.name?.toLowerCase().endsWith(".dicom")) && (
                    <span className="ml-3 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                      DICOM
                    </span>
                  )}
                  {result && (
                    <span className="ml-3 font-mono text-xs text-indigo-400">
                      {result.inference_time_ms} ms
                    </span>
                  )}
                  {result?.demo_mode && (
                    <span className="ml-3 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      DEMO MODE
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowBoxes((s) => !s)}
                    data-testid="toggle-boxes-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-800 text-xs text-zinc-300 hover:bg-zinc-900"
                  >
                    {showBoxes ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                    {showBoxes ? "Hide" : "Show"} Boxes
                  </button>
                  <button
                    onClick={clearFile}
                    data-testid="clear-image-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-zinc-800 text-xs text-zinc-300 hover:bg-zinc-900"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              </div>

              {result?.image_base64 || preview ? (
                <BoundingBoxOverlay
                  imageSrc={result?.image_base64 || preview}
                  predictions={result?.predictions || []}
                  imageWidth={result?.image_width}
                  imageHeight={result?.image_height}
                  showBoxes={showBoxes}
                  hoveredIndex={hovered}
                  onHover={setHovered}
                />
              ) : (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 py-12 text-center text-zinc-500 text-sm">
                  <div className="text-indigo-400 font-semibold mb-1">📁 DICOM file loaded</div>
                  Preview akan muncul setelah analisa (DICOM di-decode di backend).
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                {!result && (
                  <button
                    onClick={runAnalyze}
                    disabled={analyzing || !file}
                    data-testid="run-analyze-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-3 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-all"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Menganalisa X-ray...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Jalankan AI Analisa
                      </>
                    )}
                  </button>
                )}
                {result && (
                  <button
                    onClick={runSave}
                    disabled={saving}
                    data-testid="save-exam-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-5 py-3 text-sm font-medium shadow-lg shadow-emerald-500/20 transition-all"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Simpan Pemeriksaan
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Right: findings + radiologist notes */}
        <aside className="xl:col-span-3 space-y-4">
          <div className="rounded-xl border border-zinc-900 bg-zinc-900/40">
            <div className="px-5 py-4 border-b border-zinc-900">
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                AI Findings
              </div>
              <div className="font-display text-lg font-medium text-zinc-100 mt-0.5">
                {result ? `${preds.length} deteksi` : "Menunggu analisa"}
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {!result && (
                <div className="p-6 text-center text-sm text-zinc-500">
                  Belum ada hasil. Upload gambar dan jalankan analisa.
                </div>
              )}
              {result && preds.length === 0 && (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
                    <BadgeInfo className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="text-sm text-zinc-200 font-medium">
                    Tidak ditemukan abnormalitas
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    AI tidak mendeteksi temuan pada citra.
                  </div>
                </div>
              )}
              <div className="divide-y divide-zinc-900">
                {preds.map((p, i) => {
                  const conf = p.confidence ?? 0;
                  const tone =
                    conf >= 0.7
                      ? "rose"
                      : conf >= 0.4
                      ? "amber"
                      : "indigo";
                  const dot =
                    tone === "rose"
                      ? "bg-rose-500"
                      : tone === "amber"
                      ? "bg-amber-400"
                      : "bg-indigo-400";
                  const chip =
                    tone === "rose"
                      ? "text-rose-400 bg-rose-500/10"
                      : tone === "amber"
                      ? "text-amber-400 bg-amber-500/10"
                      : "text-indigo-300 bg-indigo-500/10";
                  return (
                    <div
                      key={i}
                      data-testid={`finding-item-${i}`}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      className={`px-5 py-3 cursor-pointer transition-colors ${
                        hovered === i ? "bg-zinc-800/60" : "hover:bg-zinc-900/60"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                          <span className="text-sm font-medium text-zinc-100 truncate">
                            {p.class}
                          </span>
                        </div>
                        <span
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${chip}`}
                        >
                          {(conf * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-600">
                        x:{p.x?.toFixed?.(0)} · y:{p.y?.toFixed?.(0)} · {p.width?.toFixed?.(0)}×{p.height?.toFixed?.(0)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {result && (
            <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                  Radiologist Notes
                </label>
                <textarea
                  data-testid="radiologist-notes-input"
                  value={radNotes}
                  onChange={(e) => setRadNotes(e.target.value)}
                  rows={3}
                  placeholder="Interpretasi radiolog..."
                  className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
                  Final Diagnosis
                </label>
                <textarea
                  data-testid="final-diagnosis-input"
                  value={finalDiag}
                  onChange={(e) => setFinalDiag(e.target.value)}
                  rows={2}
                  placeholder="Diagnosis akhir..."
                  className="block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none resize-none"
                />
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, testId, type = "text", placeholder, mono }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        placeholder={placeholder}
        className={`block w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
